import Swiper, {
  Lazy,
  Navigation,
  Thumbs,
  Keyboard,
  A11y,
  EffectFade,
} from 'swiper';

const breakpoints = {
  xxs: 0,
  xs: 320,
  xsm: 550,
  sm: 770,
  md: 1024,
  lg: 1230,
  xl: 1680,
  xxl: 1920,
  xxxl: 2500,
};

/**
 * Creates vehicle image carousel with event handling and syncing of multiple swipers.
 *
 * @class VehicleImageCarousel
 */
class VehicleImageCarousel {
  // JRINTCONT-767: add image view counter for analytics purposes.
  #imageViewCount = 0;
  constructor(
    thumbsSwiper,
    mainSwiper,
    fullscreenSwiper,
    disabledSlideCount,
    overlayElement
  ) {
    this.thumbsSwiper = thumbsSwiper;
    this.mainSwiper = mainSwiper;
    this.fullscreenSwiper = fullscreenSwiper;
    this.disabledSlideCount = disabledSlideCount;
    this.overlayElement = overlayElement;

    this.mainSwiper.on('click', (swiper, event) =>
      this.onMainSwiperClick(swiper, event)
    );

    this.mainSwiper.on('slideChange', swiper =>
      this.onMainSwiperSlideChange(swiper)
    );

    this.thumbsSwiper.on('slideChange', swiper => {
      this.onThumbsSwiperSlideChange(swiper);
    });

    this.fullscreenSwiper.on('slideChange', swiper =>
      this.onFullscreenSwiperSlideChange(swiper)
    );

    window.addEventListener('resize', event => this.onWindowResize(event));

    this.overlayElement.addEventListener('click', event =>
      this.onOverlayElementClick(event)
    );

    document.body.addEventListener('keydown', event =>
      this.onBodyKeydown(event)
    );
  }

  /**
   * Opens fullscreen swiper on main swiper active slide click on desktop.
   *
   * @param {Swiper} _ - Swiper instance (omitted).
   * @param {MouseEvent} event - Element click event.
   * @memberof VehicleImageCarousel
   */
  onMainSwiperClick(_, event) {
    if (
      window.innerWidth >= breakpoints.md &&
      this.mainSwiper.slides[this.mainSwiper.activeIndex].contains(event.target)
    ) {
      this.openFullscreenSwiper();
    }
  }

  /**
   * Slide past main swiper disabled slides on rewind.
   *
   * @param {Swiper} swiper - Swiper instance.
   * @memberof VehicleImageCarousel
   */
  onMainSwiperSlideChange(swiper) {
    this.rewindPastDisabledSlides(swiper);
    // JRINTCONT-767: update image view count and fire custom event whenever an image is viewed.
    this.#imageViewCount += 1;
    swiper.el.dispatchEvent(new CustomEvent('imageChange', {detail: {views: this.#imageViewCount}}));
  }

  /**
   * Translate thumbs swiper to beginning on first enabled slide.
   *
   * @param {Swiper} swiper - Swiper instance.
   * @memberof VehicleImageCarousel
   */
  onThumbsSwiperSlideChange(swiper) {
    if (swiper.activeIndex === this.disabledSlideCount) {
      swiper.translateTo(0);
    }
  }

  /**
   * Slide past fullscreen swiper disabled slides on rewind.
   *
   * @param {Swiper} swiper - Swiper instance.
   * @memberof VehicleImageCarousel
   */
  onFullscreenSwiperSlideChange(swiper) {
    this.rewindPastDisabledSlides(swiper);
  }

  /**
   * Slide past disabled slides on rewind.
   *
   * @param {Swiper} swiper - Swiper instance.
   * @memberof VehicleImageCarousel
   */
  rewindPastDisabledSlides(swiper) {
    if (swiper.activeIndex < this.disabledSlideCount) {
      if (swiper.previousIndex === swiper.slides.length - 1) {
        swiper.slideTo(this.disabledSlideCount);
      } else {
        swiper.slideTo(swiper.slides.length - 1);
      }
    }
  }

  /**
   * Closes fullscreen swiper, if open, on window resize below desktop breakpoint.
   *
   * @param {UIEvent} event - Window resize event.
   * @memberof VehicleImageCarousel
   */
  onWindowResize(event) {
    if (
      !this.fullscreenSwiper.el.hidden &&
      event.target.innerWidth < breakpoints.md
    ) {
      this.closeFullscreenSwiper();
    }
  }

  /**
   * Closes fullscreen swiper on overlay element click.
   *
   * @memberof VehicleImageCarousel
   */
  onOverlayElementClick() {
    this.closeFullscreenSwiper();
  }

  /**
   * Closes fullscreen swiper, if open, on body ESC key press.
   *
   * @param {KeyboardEvent} event - Body keydown event.
   * @memberof VehicleImageCarousel
   */
  onBodyKeydown(event) {
    if (
      !this.fullscreenSwiper.el.hidden &&
      (event.key === 'Escape' || event.key === 'Esc')
    ) {
      this.closeFullscreenSwiper();
    }
  }

  /**
   * Opens fullscreen swiper, and takes over keyboard control and active slide from main swiper.
   *
   * @memberof VehicleImageCarousel
   */
  openFullscreenSwiper() {
    this.mainSwiper.keyboard.disable();
    this.fullscreenSwiper.slideTo(this.mainSwiper.activeIndex, 0);
    this.fullscreenSwiper.keyboard.enable();
    this.fullscreenSwiper.el.hidden = false;
  }

  /**
   * Closes fullscreen swiper, and hands over keyboard control and active slide to main swiper.
   *
   * @memberof VehicleImageCarousel
   */
  closeFullscreenSwiper() {
    this.fullscreenSwiper.el.hidden = true;
    this.fullscreenSwiper.keyboard.disable();
    this.mainSwiper.slideTo(this.fullscreenSwiper.activeIndex, 0);
    this.mainSwiper.keyboard.enable();
  }
}

/**
 * Initializes vehicle image carousel.
 *
 * @return {VehicleImageCarousel} - Vehicle image carousel instance.
 */
const init = () => {
  // Initialize thumbs swiper to control main swiper
  const thumbsSwiper = new Swiper('.vehicle-image-carousel--thumbs', {
    a11y: true,
    breakpoints: {
      [breakpoints.xsm]: {
        slidesPerView: 6.5,
      },
      [breakpoints.sm]: {
        slidesPerView: 8.5,
      },
      [breakpoints.md]: {
        slidesPerView: 10.5,
      },
      [breakpoints.lg]: {
        slidesPerView: 12.5,
      },
      [breakpoints.xl]: {
        slidesPerView: 14.5,
      },
      [breakpoints.xxl]: {
        slidesPerView: 16.5,
      },
      [breakpoints.xxxl]: {
        slidesPerView: 18.5,
      },
    },
    lazy: {
      loadPrevNext: true,
    },
    modules: [A11y, Lazy],
    preloadImages: false,
    slidesPerView: 4.5,
    watchSlidesProgress: true,
  });

  // Get count of main swiper slides that cannot be swiped to, e.g. 360 image and video links
  const disabledSlideCount = document.querySelectorAll(
    '.vehicle-image-carousel--main .vehicle-image-carousel__slide--disabled'
  ).length;

  // Initialize main swiper
  const mainSwiper = new Swiper('.vehicle-image-carousel--main', {
    a11y: true,
    breakpoints: {
      [breakpoints.xsm]: {
        slidesPerView: 1.5,
      },
      [breakpoints.sm]: {
        slidesPerView: 1.75,
      },
      [breakpoints.md]: {
        slidesPerView: 2,
      },
      [breakpoints.lg]: {
        slidesPerView: 2.25,
      },
      [breakpoints.xl]: {
        slidesPerView: 2.5,
      },
      [breakpoints.xxl]: {
        slidesPerView: 2.75,
      },
      [breakpoints.xxxl]: {
        slidesPerView: 3,
      },
    },
    centeredSlides: true,
    initialSlide: disabledSlideCount,
    keyboard: true,
    lazy: {
      loadPrevNext: true,
    },
    modules: [A11y, Keyboard, Lazy, Navigation, Thumbs],
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    on: {
      init: () => thumbsSwiper.slideTo(0, 0),
    },
    preloadImages: false,
    rewind: true,
    thumbs: {
      autoScrollOffset: 0.5,
      swiper: thumbsSwiper,
    },
  });

  // Initialize fullscreen swiper
  const fullscreenSwiper = new Swiper('.vehicle-image-carousel--fullscreen', {
    a11y: true,
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
    initialSlide: disabledSlideCount,
    lazy: {
      loadPrevNext: true,
    },
    modules: [A11y, EffectFade, Keyboard, Lazy, Navigation, Thumbs],
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    preloadImages: false,
    rewind: true,
  });

  const overlayElement = document.querySelector(
    '.vehicle-image-carousel__overlay'
  );

  const vehicleImageCarousel = new VehicleImageCarousel(
    thumbsSwiper,
    mainSwiper,
    fullscreenSwiper,
    disabledSlideCount,
    overlayElement
  );

  return vehicleImageCarousel;
};

export { init };
